/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import Alert from 'bundles/authentication-common/components/Alert';
import Form from 'bundles/authentication-common/components/Form';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Submit from 'bundles/authentication-common/components/Submit';
import EmailInput from 'bundles/authentication-common/components/inputs/EmailInput';
import PasswordInput from 'bundles/authentication-common/components/inputs/PasswordInput';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import thirdPartyAuth from 'bundles/third-party-auth/lib';
import type { SSO_TYPE } from 'bundles/third-party-auth/lib';
import { getErrorMessages } from 'bundles/user-account/utils/errorMessageUtils';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  mode: 'existingCourseraAccount' | 'noEmailAvailable' | 'loginDisabledForDomain';
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  onSSOSuccess: (isRegistration: boolean) => void;
  referrer?: AUTHENTICATION_MODAL_MODES_KEY;
  service?: SSO_TYPE;
  hideBackToLink?: boolean;
  onError?: (error: string | JSX.Element) => void;
  onInvalid?: () => void;
};

type Message = {
  primary: string;
  secondary: string | React.ReactElement<typeof FormattedMessage>;
};

type MessageReturnType = {
  noEmailAvailable: Message;
  existingCourseraAccount: Message;
  loginDisabledForDomain: Message;
};

type State = {
  error?: string;
};

const styles = {
  container: css`
    display: grid;
    grid-gap: 24px;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 32px 16px;
  `,
  description: css`
    color: var(--cds-color-grey-900);
    font-size: 14px;
    line-height: 24px;
    margin: 0;
    text-align: center;
  `,
  form: css`
    display: grid;
    grid-gap: 12px;
    grid-template-columns: 1fr;
  `,
  navigation: css`
    margin: 0;
    text-align: center;
  `,
  section: css`
    :nth-child(1) {
      -ms-grid-row: 1;
    }

    :nth-child(2) {
      -ms-grid-row: 2;
    }

    :nth-child(3) {
      -ms-grid-row: 3;
    }

    :nth-child(4) {
      -ms-grid-row: 4;
    }
  `,
  title: css`
    color: var(--cds-color-neutral-primary);
    font-family: 'Open Sans', sans-serif;
    font-size: 24px;
    line-height: 30px;
    margin: 0;
    text-align: center;

    @media (min-width: 600px) {
      font-size: 34px;
      line-height: 42px;
    }
  `,
  submit: css`
    margin-top: 12px;
  `,
};

const modeMessages = (): MessageReturnType => ({
  noEmailAvailable: {
    primary: _t('Almost there!'),
    secondary: _t("We'll need your email to complete your account registration."),
  },
  existingCourseraAccount: {
    primary: _t('You already have an \n account with this email.'),
    // TODO make this link about to route locally instead of page refresh
    secondary: (
      <FormattedMessage
        message={_t("Please enter your Coursera password to log in. \n If you don't remember it, {resetPasswordLink}.")}
        resetPasswordLink={
          <a href="/reset" target="resetpw">
            {_t('reset your password')}
          </a>
        }
      />
    ),
  },
  loginDisabledForDomain: {
    primary: _t('Login with Google, Facebook, Apple, jwt are disabled for your email domain.'),
    secondary: _t('Please use Okta instead'),
  },
});

const defaultMessage = () => ({
  primary: _t('An unexpected error occurred.'),
  secondary: _t('Please try again later.'),
});

class CompleteSSO extends React.Component<Props, State> {
  state: State = {};

  setErrorCode = (errorCode?: string) => {
    this.setState({ error: errorCode });
  };

  complete = (values: $TSFixMe) => {
    const { service } = this.props;

    if (!service) {
      return;
    }

    thirdPartyAuth.connect(service, values).then(this.handleSuccess).catch(this.handleError);
  };

  handleError = (error: $TSFixMe) => {
    const { service, onError } = this.props;

    Instrumentation.thirdPartyError(service, error);

    // @ts-expect-error TS7016 TODO: error and code has type any
    this.setState({ error: getErrorMessages[error?.code] });

    if (onError) {
      onError(error);
    }
  };

  handleSuccess = (response: $TSFixMe) => {
    const { service, onSSOSuccess } = this.props;

    Instrumentation.thirdPartyAuth(service, response);

    onSSOSuccess(response.isRegistration);
  };

  navigateBack = () => {
    const { navigate, referrer } = this.props;

    return navigate(referrer || AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  navigateToForgotPassword = () => {
    const { navigate } = this.props;

    navigate(AUTHENTICATION_MODAL_MODES.FORGOT);
  };

  onInvalid = () => {
    const { onInvalid = () => {} } = this.props;

    this.setState({ error: undefined });
    onInvalid();
  };

  render() {
    const { mode, referrer, service, hideBackToLink = false } = this.props;
    const { error } = this.state;

    let message;
    message = modeMessages()[mode] ?? defaultMessage();

    const navigateTo = referrer === AUTHENTICATION_MODAL_MODES.SIGNUP ? _t('Sign Up') : _t('Log in');

    /* Our temporary solution for this scenario (https://coursera.atlassian.net/browse/GR-25354) involves showing a simple error message, with no form input options, to the user.
    We plan to improve this and provide a more permanent solution in the future. */
    const isFacebookEmailMissingScenario = service === 'facebook' && mode === 'noEmailAvailable';
    const facebookEmailMissingOverride = {
      primary: _t('Sorry, we weren’t able to complete your registration.'),
      secondary: _t('Please sign up with email or your Google account.'),
    };

    if (isFacebookEmailMissingScenario) message = facebookEmailMissingOverride;
    /* End */

    return (
      <section className="rc-CompleteFormSSO" css={styles.container}>
        <h2 css={[styles.title, styles.section]}>{message.primary}</h2>
        <p css={[styles.description, styles.section]}>{message.secondary}</p>
        <Form css={[styles.form, styles.section]} onSubmit={this.complete} onInvalid={this.onInvalid}>
          {error && <Alert type="error">{error}</Alert>}
          {!isFacebookEmailMissingScenario && mode === 'noEmailAvailable' && <EmailInput />}
          {mode === 'existingCourseraAccount' && (
            <PasswordInput
              hint={<LinkButton onClick={this.navigateToForgotPassword}>{_t('Forgot password?')}</LinkButton>}
              placeholder={_t('Enter your password')}
              autoComplete="current-password"
            />
          )}
          {!isFacebookEmailMissingScenario && ['noEmailAvailable', 'existingCourseraAccount'].includes(mode) && (
            <Submit css={styles.submit}>{_t('Continue')}</Submit>
          )}
        </Form>

        {!hideBackToLink && (
          <p css={[styles.navigation, styles.section]}>
            <FormattedMessage
              message={_t('Back to {backToLink}.')}
              backToLink={
                <LinkButton onClick={this.navigateBack} type="button">
                  {navigateTo}
                </LinkButton>
              }
            />
          </p>
        )}
      </section>
    );
  }
}

export default CompleteSSO;
